import React, {useContext, useMemo, useState} from 'react';
import usePA_GlobalStore from "../../Services/ZustandGlobalStore";
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    ListItem,
    Tab,
    Tabs
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import {PAContext} from "../../Services/PAContext";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {getEventFromDatabase} from "../../Services/BrowserDatabaseFunctions";
import dayjs from "dayjs";
import PAEvent from "../../MasterEventObjects/PAEvent";
import {useNavigate} from "react-router-dom";
import {usePoints} from "../../Services/PointsContext";


const messages = {
    meals: {
        congrats: [
            "You're keeping everyone well-fed! Excellent work planning meals.",
            "Three squares a day? You nailed the meal planning!",
            "Your event attendees will love the carefully planned meals.",
            "Meal planning is on point — the team will thank you.",
            "Outstanding! You've got the menu covered beautifully."
        ],
        recommend: [
            "Consider adding more meals to cover all days of your event.",
            "Try to ensure each day has at least three meals planned.",
            "Add variety to your meal planning to boost engagement.",
            "Remember, attendees appreciate consistent meal coverage.",
            "Increase your meal entries to meet expected engagement."
        ]
    },
    lodging: {
        congrats: [
            "Lodging is perfectly arranged — great job!",
            "Your attendees will have a comfy stay thanks to your planning.",
            "Accommodation planning is solid — well done!",
            "You’ve ensured everyone has a place to stay.",
            "Fantastic! Lodging details are complete."
        ],
        recommend: [
            "Consider verifying lodging for every night of your event.",
            "Add any missing accommodation details to maximize points.",
            "Ensure at least one lodging entry per event.",
            "Double-check your lodging arrangements to cover all attendees.",
            "Add more accommodations to meet expected engagement."
        ]
    },
    activities: {
        congrats: [
            "Activities are well-scheduled — great work!",
            "Your itinerary looks exciting and engaging.",
            "Attendees will love the planned activities.",
            "Fantastic planning — activities are covering the event nicely.",
            "You’re delivering a variety of experiences for everyone."
        ],
        recommend: [
            "Add more activities to ensure engagement on all days.",
            "Try to plan 1-2 activities per day to meet expectations.",
            "Consider diversifying activities for all attendee interests.",
            "Increase the number of scheduled activities to maximize engagement.",
            "Check that each day has at least one activity."
        ]
    },
    flights: {
        congrats: [
            "Travel plans are well-organized — excellent job!",
            "Your flight entries make the trip seamless for attendees.",
            "Flights are covered — great foresight!",
            "You’ve ensured everyone can travel efficiently.",
            "Top-notch flight planning!"
        ],
        recommend: [
            "Add or verify flight details for all attendees.",
            "Ensure each attendee’s travel is fully accounted for.",
            "Consider adding missing flight information to improve engagement.",
            "Double-check that flight schedules match the event days.",
            "Plan 1-2 flights per event to reach expected coverage."
        ]
    },
    receipts: {
        congrats: [
            "Expense tracking is solid — nice work!",
            "You’re keeping receipts organized for all activities.",
            "Great job documenting all expenses.",
            "Receipts are accounted for — excellent!",
            "Attendees can trust the records thanks to your diligence."
        ],
        recommend: [
            "Add more receipts to cover all planned activities.",
            "Ensure 2-4 receipts per day are uploaded.",
            "Check that all expenses are documented for accuracy.",
            "Remember to include receipts for meals and lodging.",
            "Increase receipt submissions to meet expected engagement."
        ]
    },
    to_do: {
        congrats: [
            "Tasks are well-managed — excellent!",
            "You’re keeping your event on track.",
            "Your to-do items cover the essential planning steps.",
            "Great job staying organized with to-do items.",
            "Your planning tasks are well-documented."
        ],
        recommend: [
            "Add more to-do items to cover all event responsibilities.",
            "Ensure every critical task is included in the to-do list.",
            "Consider assigning tasks to attendees to boost engagement.",
            "Add at least 1-3 to-do items per day.",
            "Review your tasks to ensure complete coverage."
        ]
    },
    ideas: {
        congrats: [
            "Your event is full of creative ideas — fantastic!",
            "Great job contributing innovative suggestions.",
            "Ideas are flowing — attendees will love it.",
            "Excellent participation in generating ideas.",
            "Your creativity shines in event planning."
        ],
        recommend: [
            "Encourage adding more ideas for activities or destinations.",
            "Seek input from attendees to diversify ideas.",
            "Add at least one idea per event to maximize points.",
            "Consider brainstorming additional fun or unique suggestions.",
            "Review ideas to ensure coverage across all event areas."
        ]
    },
    documents: {
        congrats: [
            "All necessary documents are in place — well done!",
            "Your planning documentation is complete.",
            "Great job keeping your event organized.",
            "Documents are thorough and ready for reference.",
            "Excellent! Attendees will have everything they need."
        ],
        recommend: [
            "Add missing documents to cover all event logistics.",
            "Ensure at least one key document per event is included.",
            "Consider uploading supporting documents for activities.",
            "Review document coverage for completeness.",
            "Increase document submissions to meet expectations."
        ]
    },
    notes: {
        congrats: [
            "Your notes are well-documented — nice work!",
            "Great job keeping track of important details.",
            "Notes are clear and helpful for attendees.",
            "Excellent attention to detail in your notes.",
            "Your record-keeping is on point."
        ],
        recommend: [
            "Add notes for any missed activities or details.",
            "Ensure at least one note per event.",
            "Use notes to track insights or observations for next events.",
            "Document important points for continuity across events.",
            "Increase note-taking to cover all critical aspects."
        ]
    },
    supplies: {
        congrats: [
            "Your supplies are well-prepared — great planning!",
            "Everything needed for the event is accounted for — excellent work!",
            "Supplies are organized and ready for attendees.",
            "Top-notch supply management — nothing will be missing.",
            "You’ve ensured all materials and resources are in place."
        ],
        recommend: [
            "Consider adding any missing supplies to meet event needs.",
            "Check that all essential items are included for the event.",
            "Ensure supplies are sufficient for all attendees.",
            "Review supply quantities and adjust if necessary.",
            "Add at least 20 supplies per day to meet expected engagement."
        ]
    },
    messages: {
        congrats: [
            "Your communication is strong — great job keeping everyone informed!",
            "Messages are timely and effective — excellent work!",
            "You’re actively engaging with attendees through messages.",
            "Fantastic job maintaining good communication throughout the event.",
            "Your messaging keeps everyone on track and informed."
        ],
        recommend: [
            "Send more messages to ensure attendees are well-informed.",
            "Engage in discussions more frequently to boost participation.",
            "Ensure key updates are communicated promptly.",
            "Consider sending reminders or confirmations via messages.",
            "Increase messaging frequency to meet expected engagement."
        ]
    },
    budget: {
        congrats: [
            "Budget is set and clearly defined — great planning!",
            "You’ve successfully allocated funds for the event.",
            "Excellent work managing event finances.",
            "Budgeting is thorough and transparent.",
            "Top-notch! Your financial planning ensures smooth operations."
        ],
        recommend: [
            "Set or review the budget to ensure all expenses are covered.",
            "Check that budget allocations are accurate for all categories.",
            "Ensure the budget is recorded for proper tracking.",
            "Consider adjusting budget allocations to meet event needs.",
            "Update budget details to reach expected engagement points."
        ]
    }
};



function UnifiedPoints({ isOpen, onClose }) {

    const navigate = useNavigate();

    const { PA_GlobalObject,  set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account } = dataArray;

    // 1. Snatch the pre-calculated global values instantly
    const {
        totalAccountPoints,
        yearlyPoints,
        loadingEvents,
        allPointsDetails,
        allEvents,
        TIERS,
        calculateSingleEventPoints,
        getCurrentTier,
        getNextTier
    } = usePoints();

    // 2. Combine them to get your total running score
    const runningScoreTotal = totalAccountPoints + yearlyPoints;


    const [tabIndex, setTabIndex] = useState(0);
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    // State for which event breakdowns are expanded
    const [expandedEventIds, setExpandedEventIds] = useState(new Set());


    // Toggle expanded/collapsed for a specific event
    function toggleEventExpansion(eventId) {
        setExpandedEventIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(eventId)) {
                newSet.delete(eventId);
            } else {
                newSet.add(eventId);
            }
            return newSet;
        });
    }

    // Prepare breakdown for a given event
    function getEventBreakdown(event) {
        if (!Account?.id || !event) return [];
        // Fixed: Passing parameters cleanly down to the globally sourced evaluator
        const { eventBreakdown } = calculateSingleEventPoints(event, Account.id);
        return eventBreakdown;
    }

    // Precompute points and breakdowns
    const precomputedEvents = useMemo(() => {
        if (!Account?.id || !allEvents || !allEvents.length) return [];

        return allEvents.map(event => ({
            ...event,
            // Fixed: Stripped eventPointsRules parameter since the global helper handles rules internally
            calculated: calculateSingleEventPoints(event, Account.id)
        }));
    }, [allEvents, Account?.id, calculateSingleEventPoints]);


    /*
        for the Analyzer
     */

    function analyzeUserEvents(events, userId) {
        // Array to store results per event
        const eventAnalysis = [];

        events.forEach(event => {
            // Extract event dates

            const dates = event.time_table?.dates || [];
            const eventStart = dates[0] ? dayjs(dates[0].date) : null;
            const eventEnd = dates[dates.length - 1] ? dayjs(dates[dates.length - 1].date) : null;
            const eventLength = eventStart && eventEnd ? eventEnd.diff(eventStart, 'day') + 1 : 1;

            // Expected counts per section based on event length
            const expectedMeals = 3 * eventLength;          // assume 3 meals per day
            const expectedLodging = 1;                      // assume 1 lodging entry per event
            const expectedActivities = 1.5 * eventLength;   // 1-2 activities per day
            const expectedFlights = 1.5 * eventLength;      // maybe 1-2 flights per event
            const expectedSupplies = 20 * eventLength;      // probably at least 20 per event
            const expectedToDo = 2 * eventLength;           // 1-3 to_do items
            const expectedReceipts = 3 * eventLength;       // 2-4 receipts per day
            const expectedIdeas = 1;                        // at least 1 idea per event
            const expectedDocuments = 1;                    // at least 1 document per event
            const expectedNotes = 1;                        // at least 1 note per event
            const expectedMessages = 7.5;                   // maybe 5-10 messages per event
            const expectedBudget = 1;                       // just set the budget once

            // Count the user's contributions
            const userMeals = Array.isArray(event.meals)
                ? event.meals.reduce((count, meal) =>
                        count + (meal.attendees?.some(att => String(att.id) === String(userId)) ? 1 : 0),
                    0)
                : 0;

            const userLodging = Array.isArray(event.lodging)
                ? event.lodging.reduce((count, lodging) =>
                        count + (lodging.attendees?.some(att => String(att.id) === String(userId)) ? 1 : 0),
                    0)
                : 0;

            const userActivities = Array.isArray(event.activities)
                ? event.activities.reduce((count, activity) =>
                        count + (activity.attendees?.some(att => String(att.id) === String(userId)) ? 1 : 0),
                    0)
                : 0;

            const userFlights = Array.isArray(event.flights)
                ? event.flights.reduce((count, flight) =>
                        count + (flight.attendees?.some(att => String(att.id) === String(userId)) ? 1 : 0),
                    0)
                : 0;

            const userSupplies = Array.isArray(event.supplies)
                ? event.supplies.filter(item => String(item.assigned_to) === String(userId)).length
                : 0;

            const userToDo = Array.isArray(event.to_do)
                ? event.to_do.filter(task => String(task.assigned_to) === String(userId)).length
                : 0;

            const userReceipts = Array.isArray(event.receipts)
                ? event.receipts.filter(r => String(r.attendee_id) === String(userId)).length
                : 0;

            const userIdeas = Array.isArray(event.ideas)
                ? event.ideas.filter(idea => String(idea.attendee_id) === String(userId)).length
                : 0;

            const userMessages = Array.isArray(event.messages)
                ? event.messages.filter(m => String(m.attendee_id) === String(userId)).length
                : 0;

            const userDocuments = Array.isArray(event.documents)
                ? event.documents.filter(doc => String(doc.attendee_id) === String(userId)).length
                : 0;

            const userNotes = Array.isArray(event.notes)
                ? event.notes.filter(note => String(note.attendee_id) === String(userId)).length
                : 0;

            const userBudget = Array.isArray(event.attendees)
                ? event.attendees.some(att => String(att.id) === String(userId) && Number(att.budget?.total_budget || 0) !== 0)
                    ? 1
                    : 0
                : 0;

            // Quantify engagement as percentage of expected
            const engagement = {
                meals: userMeals / expectedMeals,
                lodging: userLodging / expectedLodging,
                activities: userActivities / expectedActivities,
                flights: userFlights / expectedFlights,
                supplies: userSupplies / expectedSupplies,
                to_do: userToDo / expectedToDo,
                receipts: userReceipts / expectedReceipts,
                ideas: userIdeas / expectedIdeas,
                messages: userMessages / expectedMessages,
                documents: userDocuments / expectedDocuments,
                notes: userNotes / expectedNotes,
                budget: userBudget / expectedBudget,
            };

            // Store metrics for this event
            eventAnalysis.push({
                eventId: event.id,
                title: event.title,
                startDate: eventStart?.format('YYYY-MM-DD'),
                endDate: eventEnd?.format('YYYY-MM-DD'),
                lengthInDays: eventLength,
                expected: {
                    meals: expectedMeals,
                    lodging: expectedLodging,
                    activities: expectedActivities,
                    flights: expectedFlights,
                    supplies: expectedSupplies,
                    to_do: expectedToDo,
                    receipts: expectedReceipts,
                    ideas: expectedIdeas,
                    messages: expectedMessages,
                    documents: expectedDocuments,
                    notes: expectedNotes,
                    budget: expectedBudget,
                },
                actual: {
                    meals: userMeals,
                    lodging: userLodging,
                    activities: userActivities,
                    flights: userFlights,
                    supplies: userSupplies,
                    to_do: userToDo,
                    receipts: userReceipts,
                    ideas: userIdeas,
                    messages: userMessages,
                    documents: userDocuments,
                    notes: userNotes,
                    budget: userBudget
                },
                engagement, // engagement as ratio (0-1, can multiply by 100 for %)
            });

        });

        return eventAnalysis;
    }

    const [analyzedEvents, setAnalyzedEvents] = useState([]);


    function pickRandomMessage(messagesArray) {
        return messagesArray[Math.floor(Math.random() * messagesArray.length)];
    }
    function getTopCongratulationsAndRecommendations(analyzedEvents) {

        // Aggregate totals across all events
        const totals = {
            meals: 0,
            lodging: 0,
            activities: 0,
            flights: 0,
            supplies: 0,
            receipts: 0,
            to_do: 0,
            ideas: 0,
            messages: 0,
            documents: 0,
            notes: 0,
            budget: 0,
            eventsCount: 0,
            totalDays: 0,
        };

        analyzedEvents.forEach(event => {
            const days = event.lengthInDays || 1;
            totals.eventsCount += 1;
            totals.totalDays += days;
            totals.meals += event.engagement.meals || 0;
            totals.lodging += event.engagement.lodging || 0;
            totals.activities += event.engagement.activities || 0;
            totals.flights += event.engagement.flights || 0;
            totals.supplies += event.engagement.supplies || 0;
            totals.receipts += event.engagement.receipts || 0;
            totals.to_do += event.engagement.to_do || 0;
            totals.ideas += event.engagement.ideas || 0;
            totals.messages += event.engagement.messages || 0;
            totals.documents += event.engagement.documents || 0;
            totals.notes += event.engagement.notes || 0;
            totals.budget += event.engagement.budget || 0;
        });

        // Expected involvement per day (example assumptions)
        const expected = {
            meals: totals.totalDays * 3,           // 3-4 meals per day
            lodging: totals.eventsCount,           // 1 lodging per event
            activities: totals.totalDays * 2,      // 1-2 activities per day
            flights: totals.eventsCount * 2,           // 1 flight per event if travel
            supplies: totals.eventsCount * 20,           // 1 flight per event if travel
            receipts: totals.totalDays * 3,        // 2-4 receipts per day
            to_do: totals.totalDays * 2,           // 1-2 tasks per day
            ideas: totals.eventsCount,             // 1 idea per event minimum
            messages: totals.eventsCount * 5,             // maybe 5-10 messages per event
            documents: totals.eventsCount,         // 1 doc per event minimum
            notes: totals.eventsCount,             // 1 note per event minimum
            budget: totals.eventsCount,            // budget is 0 or not 0
        };

        // Compute % achievement per category
        const achievement = {};
        Object.keys(expected).forEach(key => {
            achievement[key] = expected[key] > 0 ? (totals[key] / expected[key]) * 100 : 0;
        });

        // Sort categories by % achievement
        const sortedCategories = Object.entries(achievement).sort((a, b) => b[1] - a[1]);

        // Top 3 things to congratulate
        const topCongrats = sortedCategories.slice(0, 3).map(([key, percent]) => ({
            category: key,
            achievementPercent: Math.round(percent),
            message: pickRandomMessage(messages[key].congrats),
        }));

        // Bottom 3 things to recommend
        const bottomRecommendations = sortedCategories
            .slice(-3)
            .map(([key, percent]) => ({
                category: key,
                achievementPercent: Math.round(percent),
                message: pickRandomMessage(messages[key].recommend),
            }));

        return { topCongrats, bottomRecommendations };
    }
    const { topCongrats, bottomRecommendations } = useMemo(() => {
        if (analyzedEvents.length === 0) {
            return { topCongrats: [], bottomRecommendations: [] };
        }
        return getTopCongratulationsAndRecommendations(analyzedEvents);
    }, [analyzedEvents]);
    async function loadEvent(event_id) {
        try {
            const specificEvent = await getEventFromDatabase(event_id);

            if (!specificEvent) {
                console.log("specificEvent NOT FOUND");
                return;
            }

            // Format the event
            const formattedEvent = new PAEvent(specificEvent);

            // Update the global state
            await set_PA_GlobalObject({Planning: formattedEvent});

            // Check for new messages
            const _sendString = {
                action: "E_getEventMessages",
                event_id: specificEvent.id,
                latest_message_timestamp: specificEvent.messages.length
                    ? specificEvent.messages.reduce((latest, message) => Math.max(latest, message.created), 0)
                    : 0
            };

            sendSocketMessage(_sendString);

            // Navigate to the Itinerary page if not already there
            navigate('/Planner/Itinerary');

            //close the Dialog
            onClose();

            console.log("Loaded event:", formattedEvent);
        } catch (error) {
            console.error("Error loading event:", error);
        }
    }



    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 2, pt: 2 }}>
                <DialogTitle
                    sx={{p: 0, fontWeight: 600, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: 1}}>
                    <i className="icon-trophy" style={{fontSize: '28px', color: '#ffc107'}}/> Points & Rewards
                </DialogTitle>
                <IconButton onClick={onClose} sx={{p: '5px'}}>
                    <i className="icon-cross" style={{fontSize: '28px', color: '#757575'}}/>
                </IconButton>
            </Box>

            <Divider sx={{mb: 2, mx: 2}}/>

            {/* Total Points Summary */}
            <Box sx={{
                textAlign: 'center',
                mb: 3,
                p: 2,
                mx: 2,
                backgroundColor: '#e8f2ed',
                borderRadius: '12px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                border: '1px solid #abd154'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#689f38' }}>
                    Total Points Earned:
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#33691e', mt: 1, mb: 1.5 }}>
                    {loadingEvents ? '...' : `${runningScoreTotal}`} ✨
                </Typography>
                <Typography variant="body2" sx={{ color: '#616161' }}>
                    Keep planning to earn even more!
                </Typography>
            </Box>

            {/* Sticky Tabs */}
            <Box
                sx={{
                    py: 1,
                    zIndex: 1,
                }}
            >
                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant='fullWidth'
                    scrollButtons="auto"
                    TabIndicatorProps={{ style: { display: 'none' } }}
                    sx={{
                        '& .MuiTab-root': {
                            fontWeight: 'bold',
                            minHeight: 48,
                            textTransform: 'none',
                            color: '#555',
                            px: { xs: 1, sm: 2 }, // smaller padding on mobile
                        },
                        '& .Mui-selected': {
                            color: '#1976d2',
                        },
                    }}
                >
                    <Tab
                        value={0}
                        label={
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column', // always stacked
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                {/* Points badge with icon */}
                                <Box
                                    sx={{
                                        borderRadius: '12px',
                                        background: '#fef4ec',
                                        border: '1px solid #f09637',
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' }, // icon moves below text on xs
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                        px: 1,
                                        py: 1,
                                        transition: 'transform 0.2s ease-in-out',
                                        '&:hover': { transform: 'scale(1.1)' },
                                    }}
                                >
                                    {/* Points text */}
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: '#f09637',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        {yearlyPoints} pts
                                    </Typography>

                                    {/* Icon */}
                                    <Typography sx={{ fontSize: '1rem', mt: 0.25 }}>
                                        🏅
                                    </Typography>
                                </Box>

                                {/* Label below badge */}
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        color: '#f09637',
                                        textAlign: 'center',
                                    }}
                                >
                                    Events
                                </Typography>
                            </Box>
                        }
                    />

                    <Tab
                        value={1}
                        label={
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column', // always stacked
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                {/* Points badge with icon */}
                                <Box
                                    sx={{
                                        borderRadius: '12px',
                                        background: '#fef4ec',
                                        border: '1px solid #f09637',
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' }, // icon moves below text on xs
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                        px: 1,
                                        py: 1,
                                        transition: 'transform 0.2s ease-in-out',
                                        '&:hover': { transform: 'scale(1.1)' },
                                    }}
                                >
                                    {/* Points text */}
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: '#f09637',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        {totalAccountPoints} pts
                                    </Typography>

                                    {/* Icon */}
                                    <Typography sx={{ fontSize: '1rem', mt: 0.25 }}>
                                        🏅
                                    </Typography>
                                </Box>

                                {/* Label below badge */}
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        color: '#f09637',
                                        mt: 0.25,
                                        textAlign: 'center',
                                    }}
                                >
                                    Account
                                </Typography>
                            </Box>
                        }
                    />

                    <Tab
                        value={2}
                        label={
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column', // always stacked
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                {/* Points badge with icon */}
                                <Box
                                    sx={{
                                        borderRadius: '12px',
                                        background: '#fef4ec',
                                        border: '1px solid #f09637',
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' }, // icon moves below text on xs
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                        px: 1,
                                        py: 1,
                                        transition: 'transform 0.2s ease-in-out',
                                        '&:hover': { transform: 'scale(1.1)' },
                                    }}
                                >
                                    {/* Points text */}
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: '#f09637',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        {getCurrentTier(yearlyPoints + totalAccountPoints)?.label || "Unknown"}
                                    </Typography>

                                    {/* Icon */}
                                    <Typography sx={{ fontSize: '1rem', mt: 0.25 }}>
                                        🏆
                                    </Typography>
                                </Box>

                                {/* Label below badge */}
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        color: '#f09637',
                                        mt: 0.25,
                                        textAlign: 'center',
                                    }}
                                >
                                    Tiers
                                </Typography>
                            </Box>
                        }
                    />

                    <Tab
                        value={3}
                        label={
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column', // always stacked
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                {/* Points badge with icon */}
                                <Box
                                    sx={{
                                        borderRadius: '12px',
                                        background: '#fef4ec',
                                        border: '1px solid #f09637',
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' }, // icon moves below text on xs
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                        px: 1,
                                        py: 1,
                                        transition: 'transform 0.2s ease-in-out',
                                        '&:hover': { transform: 'scale(1.1)' },
                                    }}
                                >
                                    {/* Icon */}
                                    <Typography sx={{ fontSize: '1rem', mt: 0.25 }}>
                                        ✨
                                    </Typography>
                                </Box>

                                {/* Label below badge */}
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        color: '#f09637',
                                        mt: 0.25,
                                        textAlign: 'center',
                                    }}
                                >
                                    Guide
                                </Typography>
                            </Box>
                        }
                    />
                </Tabs>
            </Box>

            <DialogContent
                dividers
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 3,
                    backgroundColor: '#f9f9f9',
                }}
            >
                {tabIndex === 0 && (
                    <>
                        {/* Current Event */}
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#424242' }}>
                            Points Breakdown: {PA_GlobalObject.Planning.title}
                        </Typography>

                        {/* put a notice here if you aren't eligible for points bc you are planner or partial attendee */}
                        {(() => {
                            const attendee = PA_GlobalObject.Planning.attendees.find(
                                (_att) => _att.id === Account.id
                            );
                            const isEligible = !['planner', 'partial'].includes(attendee?.role);
                            return !isEligible ? (
                                <Chip
                                    label={`Not eligible for points: ${attendee?.role ?? ''}`}
                                    size="small"
                                    sx={{
                                        fontSize: '0.6rem',
                                        backgroundColor: '#edb0c0',
                                        color: '#9f3d48',
                                        fontWeight: 600
                                    }}
                                />
                            ) : null;
                        })()}


                        <List disablePadding>
                            {/* FIXED: Added '|| []' fallback to prevent mapping on undefined */}
                            {(getEventBreakdown(PA_GlobalObject.Planning) || []).map((item, index) => {
                                const isHostRestricted = ['attendees', 'rsvp'].includes(item.key);

                                // FIXED: Added optional chaining (?.) in case attendees array is briefly loading/undefined
                                const isUserHost = PA_GlobalObject.Planning?.attendees?.some(
                                    att => String(att.id) === String(Account?.id) && att.role === 'admin'
                                ) || false;

                                return (
                                    <ListItem
                                        key={item.key || index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            borderBottom: '1px solid #eee',
                                            py: 1.5,
                                            '&:last-child': { borderBottom: 'none' },
                                            backgroundColor: item.isFulfilled ? '#e8f2ed' : 'transparent',
                                            borderRadius: '8px',
                                            mb: 0.5,
                                            opacity: isHostRestricted && !isUserHost ? 0.5 : 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                                            {/* FIXED: Replaced standard React.cloneElement variant to cleanly support native <i> elements */}
                                            <ListItemIcon sx={{ minWidth: '40px', mr: 1 }}>
                        <span
                            style={{
                                fontSize: '22px',
                                color: item.isFulfilled ? '#689f38' : '#9e9e9e'
                            }}
                        >
                            {item.icon}
                        </span>
                                            </ListItemIcon>

                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ fontWeight: item.isFulfilled ? 600 : 400, color: '#424242' }}>
                                                        {item.label}
                                                    </Typography>
                                                }
                                                sx={{ flexGrow: 1 }}
                                            />
                                            <Chip
                                                label={`+${item.points} pts`}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    backgroundColor: item.isFulfilled ? '#8bc34a' : '#9e9e9e',
                                                    color: 'white',
                                                    fontSize: '0.75rem',
                                                    ml: 1,
                                                    height: 24,
                                                }}
                                            />
                                        </Box>

                                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: '40px', display: 'block' }}>
                                            {item.description}
                                        </Typography>

                                        {item.example && (
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', ml: '40px', mt: 0.5, display: 'block' }}>
                                                Example: {item.example}
                                            </Typography>
                                        )}

                                        {isHostRestricted && !isUserHost && (
                                            <Typography variant="caption" sx={{ color: 'warning.main', ml: '40px', mt: 0.5, fontStyle: 'italic' }}>
                                                You will be eligible for these points once you are elevated to host.
                                            </Typography>
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>

                        {/* Other Events */}
                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600, color: '#424242' }}>
                            Other Events
                        </Typography>

                        <List disablePadding>
                            {precomputedEvents
                                .sort((a, b) => {
                                    const aDate = a.time_table?.dates?.[0]?.date ? dayjs(a.time_table.dates[0].date) : dayjs(0);
                                    const bDate = b.time_table?.dates?.[0]?.date ? dayjs(b.time_table.dates[0].date) : dayjs(0);
                                    return bDate.valueOf() - aDate.valueOf(); // reverse chronological
                                })
                                .map(event => {

                                    //if they are a planner or partial attendee then points don't count
                                    const attendee = event.attendees.find((_att) => _att.id === Account.id);
                                    const isEligible = !['planner', 'partial'].includes(attendee?.role);

                                    const isExpanded = expandedEventIds.has(event.id);
                                    const firstDate = event.time_table?.dates?.[0]?.date ? dayjs(event.time_table.dates[0].date) : null;
                                    const currentYear = dayjs().year();
                                    const countsForYearlyPoints = firstDate?.year() === currentYear;

                                    return (
                                        <React.Fragment key={event.id}>
                                            <ListItem
                                                onClick={() => toggleEventExpansion(event.id)}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    borderBottom: '1px solid #eee',
                                                    borderRadius: 2,
                                                    mb: 0.5,
                                                }}
                                            >
                                                {/* LEFT SIDE: title + stacked date + chips */}
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>

                                                    {/* Title */}
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            loadEvent(event.id);
                                                        }}
                                                    >
                                                        {event.title}
                                                    </Typography>

                                                    {/* Stacked Date */}
                                                    <Typography variant="caption" color="textSecondary">
                                                        {event.time_table?.dates?.length
                                                            ? `${dayjs(event.time_table.dates[0].date).format('MMM D, YYYY')} - ${dayjs(
                                                                event.time_table.dates[event.time_table.dates.length - 1].date
                                                            ).format('MMM D, YYYY')}`
                                                            : 'No dates'}
                                                    </Typography>

                                                    {/* Stacked Chips */}
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {(countsForYearlyPoints && isEligible) && (
                                                            <Chip
                                                                label="Counts for Yearly Points"
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.6rem',
                                                                    backgroundColor: '#e8f2ed',
                                                                    color: '#5E9F3D',
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        )}

                                                        {!isEligible && (
                                                            <Chip
                                                                label={`Not eligible for points: ${attendee?.role ?? ''}`}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.6rem',
                                                                    backgroundColor: '#edb0c0',
                                                                    color: '#9f3d48',
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>

                                                {/* RIGHT SIDE: points + chevron */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        flexShrink: 0, // prevents squeezing
                                                        ml: 2
                                                    }}
                                                >
                                                    <Chip label={`${event.calculated.totalEventPoints} pts`} size="small" />
                                                    <i className={`icon-chevron-${isExpanded ? 'up' : 'down'}`} />
                                                </Box>
                                            </ListItem>

                                            {isExpanded && (
                                                <Box sx={{ pl: 4, pr: 2, mb: 1 }}>
                                                    {event.calculated.eventBreakdown.map((item, idx) => (
                                                        <Box
                                                            key={idx}
                                                            sx={{
                                                                display: 'flex',
                                                                maxHeight: isExpanded ? '1000px' : 0, // or a large number enough for the content
                                                                overflow: 'hidden',
                                                                transition: 'max-height 0.3s ease',
                                                                justifyContent: 'space-between',
                                                                mb: 0.5,
                                                                backgroundColor: item.isFulfilled ? '#e8f2ed' : 'transparent',
                                                        }}>
                                                            <Typography variant="body2">{item.label}</Typography>
                                                            <Typography variant="body2" sx={{ color: item.isFulfilled ? '#689f38' : '#9e9e9e' }}>
                                                                +{item.points} pts
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                        </List>
                    </>
                )}


                {tabIndex === 1 && (
                    <>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#424242' }}>
                            Points Breakdown:
                        </Typography>

                        <List disablePadding>
                            {allPointsDetails.map((item, index) => (
                                <ListItem
                                    key={item.key || index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderBottom: '1px solid #eee',
                                        py: 1.5,
                                        '&:last-child': { borderBottom: 'none' },
                                        backgroundColor: item.isFulfilled ? 'rgba(230, 238, 157, 0.2)' : 'transparent', // Highlight fulfilled items
                                        borderRadius: '8px',
                                        mb: 0.5,
                                        transition: 'background-color 0.2s ease-in-out',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: '40px', mr: 1 }}>
                                        {item.isFulfilled ? (
                                            React.cloneElement(item.icon, { color: "success", sx: { fontSize: '22px' } })
                                        ) : (
                                            React.cloneElement(item.icon, { color: "disabled", sx: { fontSize: '22px' } }) // Use disabled color for incomplete
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1" sx={{ fontWeight: item.isFulfilled ? 600 : 400, color: item.isFulfilled ? '#424242' : '#757575' }}>
                                                {item.label}
                                            </Typography>
                                        }
                                        secondary={ // Added secondary text for the notes
                                            item.notes && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {item.notes}
                                                </Typography>
                                            )
                                        }
                                    />
                                    <Chip
                                        label={`+${item.isFulfilled ? item.earned : item.potentialPoints} Points`} // Show earned or potential points
                                        size="small"
                                        sx={{
                                            backgroundColor: item.isFulfilled ? '#8bc34a' : '#bdbdbd', // Green for fulfilled, grey for unfulfilled
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            ml: 1,
                                            px: 1,
                                            py: 0.5,
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {tabIndex === 2 && (
                    <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 3, backgroundColor: '#fff' }}>
                        {(() => {
                            const totalPoints = yearlyPoints + totalAccountPoints;
                            const currentTier = getCurrentTier(totalPoints);

                            if (!currentTier) return null;

                            // Fixed: Tweaked colors slightly so they are readable and distinct
                            const tierColors = {
                                Explorer: '#FAF3CC',
                                Navigator: '#e8f2ed',
                                'Trail Seeker': '#fef4ec',
                                Globetrotter: '#cbe0f7',
                                'Trail Blazer': '#90caf9', // Changed from dark blue to an elegant light blue so the black marker stays perfectly visible!
                            };

                            // Determine total range for all tiers
                            const finiteMax = Math.max(...TIERS.filter(t => t.max !== Infinity).map(t => t.max));
                            const totalMaxPoints = finiteMax * 1.2; // Add buffer for last tier so it's visible

                            // Map tiers to percentage widths
                            const segments = TIERS.map(tier => {
                                const start = tier.min;
                                const end = tier.max === Infinity ? totalMaxPoints : tier.max;
                                const widthPercent = ((end - start) / totalMaxPoints) * 100;
                                return { ...tier, widthPercent };
                            });

                            // Marker for user's current points (capped at 100%)
                            const progressPercent = Math.min((totalPoints / totalMaxPoints) * 100, 100);

                            return (
                                <>
                                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                mt: 0.5,
                                                width: '100%',
                                                height: 12,
                                                borderRadius: 6,
                                                display: 'flex',
                                                overflow: 'hidden' // Added: Ensures the internal color boxes respect the container's rounded corner wrapper shape
                                            }}
                                        >
                                            {segments.map((seg, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        width: `${seg.widthPercent}%`,
                                                        backgroundColor: tierColors[seg.label],
                                                        // FIXED: Replaced layout crashing 'darken' function with standard native border overlays
                                                        border: '1px solid rgba(0, 0, 0, 0.12)',
                                                        '&:not(:last-child)': { borderRight: 'none' },
                                                        height: '100%',
                                                    }}
                                                />
                                            ))}

                                            {/* Marker for current points */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: `${progressPercent}%`,
                                                    top: -2,
                                                    transform: 'translateX(-50%)',
                                                    width: 6,
                                                    height: 16,
                                                    borderRadius: 1,
                                                    backgroundColor: '#000',
                                                    zIndex: 2, // Added: Guarantees the location pin marker stays floating cleanly above your colored multi-segment blocks
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)' // Added: High contrast shadowing so the pin jumps out at the user
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Full tier list */}
                                    <Box sx={{ width: '100%' }}>
                                        {TIERS.map((tier, index) => {
                                            const isUserCurrentTier = totalPoints >= tier.min && totalPoints <= tier.max;

                                            return (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItem: 'center',
                                                        p: 1,
                                                        borderRadius: 2,
                                                        backgroundColor: isUserCurrentTier
                                                            ? 'rgba(139,195,74,0.1)' // Soft light green backdrop highlight for active status
                                                            : 'transparent',
                                                        mb: 0.5,
                                                        borderLeft: `6px solid ${tierColors[tier.label]}`,
                                                        pl: 1.5,
                                                        transition: 'all 0.2s ease-in-out' // Smooth color transition on points update
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: isUserCurrentTier ? 700 : 500, // Make the user's active row punch out heavily
                                                            color: isUserCurrentTier ? '#2e7d32' : 'text.primary' // Green accent wording when selected
                                                        }}
                                                    >
                                                        {tier.label}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: isUserCurrentTier ? 600 : 400,
                                                            color: isUserCurrentTier ? '#2e7d32' : 'text.secondary'
                                                        }}
                                                    >
                                                        {tier.max === Infinity ? `${tier.min}+ pts` : `${tier.min} - ${tier.max} pts`}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </>
                            );
                        })()}

                        {/* Show the recommendations */}
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Top Congratulations 🎉
                            </Typography>
                            {topCongrats.length > 0 ? (
                                topCongrats.map((item, index) => (
                                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                        • {item.message}
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No standout achievements yet.
                                </Typography>
                            )}

                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                Recommendations 💡
                            </Typography>
                            {bottomRecommendations.length > 0 ? (
                                bottomRecommendations.map((item, index) => (
                                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                        • {item.message}
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No suggestions at this time.
                                </Typography>
                            )}
                        </Box>


                    </Box>
                )}

                {tabIndex === 3 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        {/*  show the details of the guide  */}
                        {


                        }
                    </Box>
                )}

            </DialogContent>
        </Dialog>
    )
}

export default UnifiedPoints;